import { ChangeDetectorRef, Component, Input, OnChanges, SimpleChanges, OnInit, OnDestroy } from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormControl,
  ReactiveFormsModule,
  UntypedFormGroup,
  Validators
} from '@angular/forms';
import { ButtonGroupItem, hasActiveButtonItem } from '../../../../model/button-group-item';
import { ValueLabelItem } from '../../../../model/value-label-item';
import { UppComponentNames, UppViewNames } from '../../../../service/model';
import {
  SECTION_VALIDATION_MESSAGES,
  TWO_SYMBOLS_REQUIRED
} from '../../../../service/model/common/validation-messages';
import { twoAlphaNumericRegex } from '../../../../service/model/common/validators';
import { CarrierDataUi, FlightServiceElements } from '../../../model';
import { carrierCodeValidator } from '../../validators/carrier-code-validator';
import { LookupOptions } from '../../../../core/util/lookup-options';
import { PERMISSION_VIEW_FAMILY } from '../../../../service/user-permissions.service';
import { UserDetailsService } from '../../../../service/user-details.service';
import { Subscription } from 'rxjs';
import { Store, select } from '@ngrx/store';
import { selectAvailableAirFamilies } from '../../../../store/families/air/air-families-selector';
import { SimplifiedAirFamily } from '../../../../families/air/model/air-family';

import { NgbTooltip } from '@ng-bootstrap/ng-bootstrap';
import { ButtonGroupComponent } from '../../../../components/button-group/button-group.component';
import { LookupSelectComponent } from '../../../../components/lookup-select/lookup-select.component';
import { ValidationErrorMessageComponent } from '../../../../components/validation-error-message/validation-error-message.component';
import { ConfigurationService } from '../../../../service/configuration/configuration.service';
import { FeatureFlags } from '../../../../core/util/resources';

@Component({
  selector: 'ama-ng-upp-airline-scope-selector',
  templateUrl: './airline-scope-selector.component.html',
  standalone: true,
  imports: [
    ButtonGroupComponent,
    ValidationErrorMessageComponent,
    LookupSelectComponent,
    ReactiveFormsModule,
    NgbTooltip
  ]
})
export class AirlineScopeSelectorComponent implements OnChanges, OnInit, OnDestroy {
  @Input() formGroup!: UntypedFormGroup;
  @Input() carrierData?: CarrierDataUi;
  @Input() multiple = false;
  @Input() carrierCodeLabel = $localize`:@upp.global.criteria.carrierCode.label: Carrier code`;
  @Input() allianceLabel = $localize`:@upp.global.criteria.alliance.label: Alliance`;
  @Input() airlineFamilyLabel = $localize`:@upp.global.criteria.airlineFamilies.label: Air family`;
  @Input() transportModesLabel = $localize`:@upp.global.criteria.transportModes.label: Mode of transport`;
  @Input() disabled = false;
  @Input() hasNDCContentType = true;
  @Input() hasEdifactContentType = true;
  @Input() flightDetailsId?: string;
  @Input() flightsNdcParity: boolean | null = null;
  @Input() showTransportModes = false;

  private readonly subscription: Subscription = new Subscription();

  CARRIER_CODE_TOOLTIP: string = $localize`:@@upp.common.carrierCodeTooltip:Entering YY as carrier will add all carriers to the rule.`;

  availableAlliances: ValueLabelItem<string>[] = [
    {
      value: '*A',
      label: $localize`:@@upp.global.criteria.alliance.starAlliance:Star Alliance`
    },
    {
      value: '*O',
      label: $localize`:@@upp.global.criteria.alliance.oneWorld:Oneworld`
    },
    {
      value: '*S',
      label: $localize`:@@upp.global.criteria.alliance.skyteam:Skyteam`
    }
  ];

  availableTransportModes: ValueLabelItem<string>[] = [
    {
      value: 'Air',
      label: $localize`:@@upp.global.criteria.transportModes.air:Air`
    },
    {
      value: 'Bus',
      label: $localize`:@@upp.global.criteria.transportModes.bus:Bus`
    },
    {
      value: 'Coach',
      label: $localize`:@@upp.global.criteria.transportModes.coach:Coach`
    },
    {
      value: 'NonAir',
      label: $localize`:@@upp.global.criteria.transportModes.nonAir:Non-air`
    },
    {
      value: 'Train',
      label: $localize`:@@upp.global.criteria.transportModes.train:Rail`
    },
    {
      value: 'Metro',
      label: $localize`:@@upp.global.criteria.transportModes.metro:Subway`
    },
    {
      value: 'Pedestrian',
      label: $localize`:@@upp.global.criteria.transportModes.pedestrian:Walking`
    }
  ];

  airlineScopeButtonGroupItems: ButtonGroupItem[] = [];
  airlineScopeButtonGroupMap: { [key: string]: ButtonGroupItem } = {};
  availableAirlineFamilyNames: string[] = [];
  availableAirlineFamilies: SimplifiedAirFamily[] = [];

  UppComponentNames = UppComponentNames;
  SECTION_VALIDATION_MESSAGES = SECTION_VALIDATION_MESSAGES;
  carrierCodesValidationMessages = {
    ...TWO_SYMBOLS_REQUIRED,
    carrierCodeValidator: () =>
      $localize`:@@upp.validation.yyValueInCarrierCode:YY covers all airlines and cannot be combined with other airline codes`
  };

  carrierTransportModesActivated = false;
  flightsUiChanges = false;

  constructor(
    private readonly formBuilder: FormBuilder,
    private readonly changeDetectorRef: ChangeDetectorRef,
    private readonly userDetailsService: UserDetailsService,
    private readonly store: Store,
    private readonly configurationService: ConfigurationService
  ) {
    this.subscription?.add(
      this.configurationService.getParameter$(FeatureFlags.carrierTransportModes).subscribe((carrierTransportModes) => {
        this.carrierTransportModesActivated = carrierTransportModes ?? false;
      })
    );
    this.subscription?.add(
      this.configurationService.getParameter$(FeatureFlags.flightsUiChanges).subscribe((flightsUiChanges) => {
        this.flightsUiChanges = flightsUiChanges ?? false;
      })
    );
  }

  get hasSelected(): AbstractControl | null {
    return this.formGroup.get('hasSelected');
  }

  ngOnInit(): void {
    this.getAirlineFamiliesLookupOptions();

    this.subscription.add(
      this.store.pipe(select(selectAvailableAirFamilies)).subscribe((result) => {
        if (result) {
          this.availableAirlineFamilies = result.map(({ name, id }) => ({ name, id }));
          this.availableAirlineFamilyNames = result.map((family) => family.name);
          this.formGroup.get('availableAirlineFamilies')?.setValue(this.availableAirlineFamilies);
        }
      })
    );
  }

  ngOnChanges(_changes: SimpleChanges): void {
    if (this.flightsUiChanges || (this.hasNDCContentType && !this.flightsNdcParity)) {
      this.airlineScopeButtonGroupItems = this.generateAirlineScopeButtonGroupItems().filter(
        (item: ButtonGroupItem) => item.name !== FlightServiceElements.ALLIANCES
      );
    } else {
      this.airlineScopeButtonGroupItems = this.generateAirlineScopeButtonGroupItems();
    }

    this.airlineScopeButtonGroupMap = this.airlineScopeButtonGroupItems.reduce((acc: any, item: any) => {
      acc[item.name] = item;
      return acc;
    }, {});

    this.formGroup.setControl('hasSelected', this.formBuilder.control(false, Validators.requiredTrue));

    if (this.carrierData?.carrierCodes) {
      this.airlineScopeButtonGroupMap.carrierCodes.active = true;
      this.onAirlineScopeToggle(this.airlineScopeButtonGroupMap.carrierCodes, this.carrierData?.carrierCodes);
    } else if (this.carrierData?.alliances && this.airlineScopeButtonGroupMap.alliances) {
      this.airlineScopeButtonGroupMap.alliances.active = true;
      this.onAirlineScopeToggle(this.airlineScopeButtonGroupMap.alliances, this.carrierData?.alliances);
    } else if (this.carrierData?.carriersFamily) {
      this.airlineScopeButtonGroupMap.carriersFamily.active = true;
      this.onAirlineScopeToggle(this.airlineScopeButtonGroupMap.carriersFamily, this.carrierData?.carriersFamily);
    }

    if (
      (this.carrierTransportModesActivated || this.flightsUiChanges) &&
      this.carrierData?.transportModes &&
      this.airlineScopeButtonGroupMap.transportModes
    ) {
      this.airlineScopeButtonGroupMap.transportModes.active = true;
      this.onAirlineScopeToggle(this.airlineScopeButtonGroupMap.transportModes, this.carrierData?.transportModes);
    }
  }

  ngOnDestroy(): void {
    this.subscription.unsubscribe();
  }

  onAirlineScopeToggle(item: ButtonGroupItem, value?: string[] | string): void {
    if (this.carrierTransportModesActivated || this.flightsUiChanges) {
      if (item.name !== FlightServiceElements.TRANSPORT_MODES) {
        const selectedItems = this.airlineScopeButtonGroupItems.filter(
          (buttonGroupItem) =>
            buttonGroupItem !== item && buttonGroupItem.name !== FlightServiceElements.TRANSPORT_MODES
        );

        selectedItems.forEach((buttonGroupItem) => (buttonGroupItem.disabled = item.active ? true : this.disabled));
      }
    } else {
      const selectedItems = this.airlineScopeButtonGroupItems.filter((buttonGroupItem) => buttonGroupItem !== item);

      selectedItems.forEach((buttonGroupItem) => (buttonGroupItem.disabled = item.active ? true : this.disabled));
    }

    if (item.active) {
      const validators = [Validators.required];
      if (item.name === FlightServiceElements.CARRIER_CODES) {
        validators.push(Validators.pattern(twoAlphaNumericRegex));
        validators.push(carrierCodeValidator);
      }
      if (item.name === FlightServiceElements.AIRLINE_FAMILIES) {
        this.formGroup.setControl('availableAirlineFamilies', this.formBuilder.control(this.availableAirlineFamilies));
      }
      if (
        (this.carrierTransportModesActivated || this.flightsUiChanges) &&
        item.name === FlightServiceElements.TRANSPORT_MODES
      ) {
        this.formGroup.setControl('transportModes', this.formBuilder.control(this.availableTransportModes));
      }
      this.formGroup.setControl(
        item.name,
        this.formBuilder.control({ value: value ?? null, disabled: this.disabled }, validators)
      );
    } else {
      this.formGroup.removeControl(item.name);
    }

    this.hasSelected?.setValue(hasActiveButtonItem(this.airlineScopeButtonGroupItems));
    this.hasSelected?.markAsTouched();
    this.changeDetectorRef.detectChanges();
  }

  private generateAirlineScopeButtonGroupItems(): ButtonGroupItem[] {
    const airlineScopeDefaultButtonGroupItems = [
      {
        name: FlightServiceElements.CARRIER_CODES,
        active: false,
        disabled: this.disabled,
        title: $localize`:@@upp.global.criteria.carrierCode.label:Carrier code`
      },
      {
        name: FlightServiceElements.ALLIANCES,
        active: false,
        disabled: this.disabled,
        title: $localize`:@@upp.global.criteria.alliance.label:Alliance`
      },
      {
        name: FlightServiceElements.AIRLINE_FAMILIES,
        active: false,
        disabled: this.disabled,
        title: $localize`:@@upp.global.criteria.airlineFamilies.label:Air family`
      }
    ];

    if (
      this.showTransportModes &&
      ((this.carrierTransportModesActivated && this.hasNDCContentType && !this.hasEdifactContentType) ||
        (this.hasEdifactContentType && !this.hasNDCContentType && this.flightsUiChanges) ||
        (this.hasNDCContentType &&
          this.hasEdifactContentType &&
          this.flightsUiChanges &&
          this.carrierTransportModesActivated))
    ) {
      airlineScopeDefaultButtonGroupItems.push({
        name: FlightServiceElements.TRANSPORT_MODES,
        active: false,
        disabled: this.disabled,
        title: $localize`:@upp.global.criteria.transportModes.label: Mode of transport`
      });
    }

    return airlineScopeDefaultButtonGroupItems;
  }

  getControl(name: string): FormControl {
    return this.formGroup.get(name) as FormControl;
  }

  getAirlineFamiliesLookupOptions(): LookupOptions | undefined {
    if (this.canLookupAirlineFamilies()) {
      return {
        destinationComponent: `${UppComponentNames.FAMILIES}/air`,
        sourceComponent: UppComponentNames.FLIGHTS,
        sourceView: this.flightDetailsId ? UppViewNames.MODIFY : UppViewNames.CREATE,
        fieldPath: `carriersFamily`,
        singleSelect: !this.multiple
      };
    }
    return undefined;
  }

  private canLookupAirlineFamilies(): boolean {
    const permissions = this.userDetailsService.loggedInUser?.permissions;
    return permissions?.some((p) => p === PERMISSION_VIEW_FAMILY) ?? false;
  }
}
